import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE as API, getToken } from '../config';

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  description: string;
  url: string;
  category?: string;
  salary?: string;
  job_type?: string;
  work_mode?: string;
  published_at?: string;
  source: string;
}

interface JobAnalysis {
  fit_score: number;
  fit_summary: string;
  salary: { stated: boolean; range: string; note?: string };
  work_mode: 'remote' | 'hybrid' | 'onsite' | 'unclear';
  visa: { likely_needed: boolean | null; note: string };
  matched: string[];
  missing: string[];
  advice: string;
}

const JOB_TYPES = [
  ['', 'All types'],
  ['contract', 'Contract / Freelance'],
  ['full-time', 'Full-time'],
  ['part-time', 'Part-time'],
  ['internship', 'Internship'],
] as const;

const WORK_MODES = [
  ['', 'Any work mode'],
  ['remote', 'Remote'],
  ['hybrid', 'Hybrid'],
  ['onsite', 'On-site'],
] as const;

// Single-country filter (users search one market at a time), applied at the
// source. City is free text — sources have no uniform city taxonomy.
const COUNTRIES = [
  'Austria', 'Belgium', 'Canada', 'Denmark', 'Finland', 'France', 'Germany',
  'Greece', 'Ireland', 'Italy', 'Netherlands', 'Norway', 'Poland', 'Portugal',
  'Romania', 'Spain', 'Sweden', 'Switzerland', 'United Kingdom', 'United States',
];

const SORTS = [
  ['relevance', 'Relevance'],
  ['fit', 'Best fit first'],
  ['newest', 'Newest first'],
] as const;

const POSTED = [
  ['0', 'Any date'],
  ['1', 'Last 24h'],
  ['7', 'Last 7 days'],
  ['30', 'Last 30 days'],
] as const;

const WORK_MODE_STYLE: Record<string, string> = {
  remote: 'bg-green-100 text-green-800',
  hybrid: 'bg-amber-100 text-amber-800',
  onsite: 'bg-blue-100 text-blue-800',
  unclear: 'bg-gray-100 text-gray-600',
};

function fitColor(score: number): string {
  if (score >= 75) return 'text-green-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-600';
}

function timeAgo(iso?: string): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms) || ms < 0) return null;
  const days = Math.floor(ms / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`;
}

// Instant keyword-overlap pre-score vs the saved profile (no LLM — the deep
// "Analyze fit" stays on demand). Title hits weigh double.
function preFit(job: Job, terms: string[]): number | null {
  if (!terms.length) return null;
  const title = job.title.toLowerCase();
  const desc = job.description.toLowerCase();
  let got = 0;
  for (const t of terms) {
    if (title.includes(t)) got += 2;
    else if (desc.includes(t)) got += 1;
  }
  return Math.round((100 * got) / (2 * terms.length));
}

// Survive navigation away and back (Tailor CV → back) within the session.
const STASH_KEY = 'jobsPageState';

function loadStash(): any {
  try { return JSON.parse(sessionStorage.getItem(STASH_KEY) || 'null') || {}; } catch { return {}; }
}

const PAGE = 100;
const AUTO_LOAD_CAP = 300; // max jobs auto-fetched when sorting by fit/newest

const JobsPage: React.FC = () => {
  const navigate = useNavigate();
  const stash = loadStash();
  const [query, setQuery] = useState<string>(stash.query || '');
  const [jobs, setJobs] = useState<Job[]>(stash.jobs || []);
  const [total, setTotal] = useState<number>(stash.total || 0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searched, setSearched] = useState<boolean>(!!stash.searched);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [profileTerms, setProfileTerms] = useState<string[]>([]);
  const [analyses, setAnalyses] = useState<Record<string, JobAnalysis>>(stash.analyses || {});
  const [analyzing, setAnalyzing] = useState<Record<string, boolean>>({});
  const [country, setCountry] = useState<string>(stash.country || '');
  const [city, setCity] = useState<string>(stash.city || '');
  const [jobType, setJobType] = useState<string>(stash.jobType || '');
  const [workMode, setWorkMode] = useState<string>(stash.workMode || '');
  const [posted, setPosted] = useState<string>(stash.posted || '0');
  const [sortBy, setSortBy] = useState<string>(stash.sortBy || 'relevance');
  const [applied, setApplied] = useState<Record<string, boolean>>(stash.applied || {});
  const [applying, setApplying] = useState<Record<string, boolean>>({});
  const [semScores, setSemScores] = useState<Record<string, number>>(stash.semScores || {});

  // Semantic fit (bge-m3 embeddings, multilingual) for any loaded job that
  // lacks a score. Falls back to keyword overlap until scores arrive.
  useEffect(() => {
    const unscored = jobs
      .filter(j => semScores[j.id] === undefined)
      .map(j => ({ id: j.id, title: j.title, description: j.description }));
    if (!unscored.length) return;
    const t = setTimeout(() => {
      fetch(`${API}/api/jobs/rank`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobs: unscored }),
      })
        .then(r => r.json())
        .then(d => { if (d.scores) setSemScores(s => ({ ...s, ...d.scores })); })
        .catch(() => {}); // no profile / AI unavailable → keyword fallback stands
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  useEffect(() => {
    try {
      sessionStorage.setItem(STASH_KEY, JSON.stringify({
        query, jobs: jobs.slice(0, 300), total, searched, analyses, semScores,
        country, city, jobType, workMode, posted, sortBy, applied,
      }));
    } catch {} // quota errors just lose the stash, never break the page
  }, [query, jobs, total, searched, analyses, semScores, country, city, jobType, workMode, posted, sortBy, applied]);

  // One-shot handoff from the Dashboard: filters were stashed, run immediately.
  useEffect(() => {
    if (!stash.autorun) return;
    try {
      const s = loadStash();
      delete s.autorun;
      sessionStorage.setItem(STASH_KEY, JSON.stringify(s));
    } catch {}
    runSearch(stash.query || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Profile → suggested keyword chips + terms for the local fit pre-score.
  useEffect(() => {
    fetch(`${API}/api/cv-profile/`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(p => {
        if (!p) return;
        const kws: string[] = [];
        if (p.current_title) kws.push(p.current_title);
        for (const t of p.target_titles ?? []) kws.push(t);
        for (const s of [...(p.skills?.technical ?? []), ...(p.skills?.tools ?? [])]) kws.push(s);
        const seen = new Set<string>();
        const unique = kws.filter(k => {
          const key = k.toLowerCase().trim();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setKeywords(unique.slice(0, 10));
        setProfileTerms(unique.map(k => k.toLowerCase().trim()));
      })
      .catch(() => {});
  }, []);

  const searchUrl = (q: string, offset: number) =>
    `${API}/api/jobs/search?q=${encodeURIComponent(q)}&limit=${PAGE}&offset=${offset}` +
    (country ? `&location=${encodeURIComponent(country)}` : '') +
    (jobType ? `&type=${encodeURIComponent(jobType)}` : '') +
    (workMode ? `&mode=${encodeURIComponent(workMode)}` : '') +
    (city.trim() ? `&city=${encodeURIComponent(city.trim())}` : '') +
    (posted !== '0' ? `&posted=${posted}` : '');

  const runSearch = async (q: string) => {
    if (!q.trim() && !country && !jobType && !workMode && !city.trim()) {
      toast.error('Type keywords or set at least one filter.');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await fetch(searchUrl(q, 0), {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Search failed');
      setJobs(data.jobs || []);
      setTotal(data.total ?? (data.jobs || []).length);
    } catch (err: any) {
      toast.error(err.message || 'Job search failed');
      setJobs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const search = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(query);
  };

  const searchKeyword = (kw: string) => {
    setQuery(kw);
    runSearch(kw);
  };

  const fetchPage = async (offset: number): Promise<{ jobs: Job[]; total: number }> => {
    const res = await fetch(searchUrl(query, offset), {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Search failed');
    return { jobs: data.jobs || [], total: data.total ?? 0 };
  };

  const appendJobs = (current: Job[], extra: Job[]): Job[] => {
    const known = new Set(current.map(j => j.id));
    return [...current, ...extra.filter(j => !known.has(j.id))];
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const page = await fetchPage(jobs.length);
      setJobs(appendJobs(jobs, page.jobs));
      setTotal(page.total || total);
    } catch (err: any) {
      toast.error(err.message || 'Could not load more');
    } finally {
      setLoadingMore(false);
    }
  };

  // Fit/newest sorting only makes sense over everything available — auto-load
  // remaining pages (capped) before the user scrolls a partially-sorted list.
  const changeSort = async (s: string) => {
    setSortBy(s);
    if (s === 'relevance' || jobs.length >= Math.min(total, AUTO_LOAD_CAP)) return;
    setLoadingMore(true);
    try {
      let acc = jobs;
      let t = total;
      while (acc.length < Math.min(t, AUTO_LOAD_CAP)) {
        const page = await fetchPage(acc.length);
        if (!page.jobs.length) break;
        acc = appendJobs(acc, page.jobs);
        t = page.total || t;
      }
      setJobs(acc);
      setTotal(t);
    } catch {} finally {
      setLoadingMore(false);
    }
  };

  const analyze = async (job: Job) => {
    setAnalyzing(a => ({ ...a, [job.id]: true }));
    try {
      const res = await fetch(`${API}/api/jobs/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: job.title,
          company: job.company,
          location: job.location,
          description: job.description,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Analysis failed');
      setAnalyses(a => ({ ...a, [job.id]: data.analysis }));
    } catch (err: any) {
      toast.error(err.message || 'Analysis failed');
    } finally {
      setAnalyzing(a => ({ ...a, [job.id]: false }));
    }
  };

  // Record "I applied to this" — feeds the Applications page.
  const markApplied = async (job: Job) => {
    setApplying(a => ({ ...a, [job.id]: true }));
    try {
      const res = await fetch(`${API}/api/jobs/applications`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_title: job.title,
          company: job.company,
          location: job.location,
          job_url: job.url,
          platform: job.source,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not record the application');
      setApplied(a => ({ ...a, [job.id]: true }));
      toast.success('Recorded in Applications.');
    } catch (err: any) {
      toast.error(err.message || 'Could not record the application');
    } finally {
      setApplying(a => ({ ...a, [job.id]: false }));
    }
  };

  // Open the generator pre-filled with this job.
  const tailorCV = (job: Job) => {
    navigate('/app/generate', {
      state: {
        job_title: job.title,
        company: job.company,
        job_description: job.description,
      },
    });
  };

  const fitOf = (job: Job) => semScores[job.id] ?? preFit(job, profileTerms);

  const displayJobs = [...jobs];
  if (sortBy === 'fit') {
    displayJobs.sort((a, b) => (fitOf(b) ?? -1) - (fitOf(a) ?? -1));
  } else if (sortBy === 'newest') {
    displayJobs.sort((a, b) => (b.published_at ?? '').localeCompare(a.published_at ?? ''));
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Find jobs</h1>
      <p className="text-gray-600 mb-4">
        Set your filters, search, then sort by fit — and analyze before applying.
      </p>

      <form onSubmit={search} className="bg-white border border-gray-200 rounded-xl p-4 mb-3 space-y-3">
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Keywords — e.g. DevOps, Python (optional with filters)"
            className="flex-1 min-w-[220px] px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoFocus
          />
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={country} onChange={e => setCountry(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
            <option value="">🌍 All countries</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            type="text"
            value={city}
            onChange={e => setCity(e.target.value)}
            placeholder="City (optional)"
            title="Prioritises jobs in this city, but still includes remote and country-wide roles you could take from there"
            className="w-36 px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <select value={workMode} onChange={e => setWorkMode(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
            {WORK_MODES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
          <select value={jobType} onChange={e => setJobType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
            {JOB_TYPES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
          <select value={posted} onChange={e => setPosted(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            title="Postings without a date are hidden when a freshness filter is set">
            {POSTED.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
          <span className="text-xs text-gray-400 self-center">
            Empty filters = search everything. Filters apply on Search.
          </span>
        </div>
      </form>

      {keywords.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-400 mb-1.5">Suggested from your profile:</p>
          <div className="flex flex-wrap gap-1.5">
            {keywords.map(kw => (
              <button
                key={kw}
                onClick={() => searchKeyword(kw)}
                className="text-xs px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition"
              >
                {kw}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* LinkedIn/Indeed have no public search APIs (and scraping breaks their
          ToS) — offer the same query on their sites in one click instead. */}
      {searched && query.trim() && (
        <div className="mb-4 text-xs text-gray-400 flex flex-wrap items-center gap-3">
          <span>Also search on:</span>
          <a className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer"
            href={`https://www.linkedin.com/jobs/search/?keywords=${encodeURIComponent(query)}`}>LinkedIn ↗</a>
          <a className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer"
            href={`https://www.indeed.com/jobs?q=${encodeURIComponent(query)}`}>Indeed ↗</a>
          <a className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer"
            href={`https://www.infojobs.net/jobsearch/search-results/list.xhtml?keyword=${encodeURIComponent(query)}`}>InfoJobs ↗</a>
          <a className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer"
            href={`https://www.tecnoempleo.com/busqueda-empleo.php?te=${encodeURIComponent(query)}`}>Tecnoempleo ↗</a>
        </div>
      )}

      {searched && !loading && jobs.length === 0 && (
        <p className="text-gray-500 text-center py-12">No jobs found. Try a broader term or fewer filters.</p>
      )}

      {jobs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select value={sortBy} onChange={e => changeSort(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            {SORTS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
          <span className="text-xs text-gray-400">
            {loadingMore ? 'Loading all results for sorting…'
              : total > jobs.length
                ? `${jobs.length} loaded of ${total} found so far`
                : `${jobs.length} jobs`}
          </span>
        </div>
      )}

      <div className="space-y-4">
        {displayJobs.map((job) => {
          const analysis = analyses[job.id];
          const fit = fitOf(job);
          return (
            <div key={job.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition">
              <div className="flex justify-between items-start gap-4">
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {fit !== null && (
                      <span className={`mr-2 text-sm font-bold ${fitColor(fit)}`}
                        title={semScores[job.id] !== undefined
                          ? 'Semantic match with your profile (multilingual embeddings) — run Analyze fit for the deep assessment'
                          : 'Keyword match with your profile (semantic score loading…)'}>
                        {fit}%
                      </span>
                    )}
                    {job.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {job.company} · {job.location}
                    {timeAgo(job.published_at) && (
                      <span className="ml-2 text-xs text-gray-400">{timeAgo(job.published_at)}</span>
                    )}
                    <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                      {job.source.replace(/^adzuna-/, 'adzuna ')}
                    </span>
                    {job.work_mode && (
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${WORK_MODE_STYLE[job.work_mode] ?? ''}`}>
                        {job.work_mode}
                      </span>
                    )}
                    {job.job_type && (
                      <span className="ml-2 text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">
                        {job.job_type}
                      </span>
                    )}
                    {job.salary && (
                      <span className="ml-2 text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full">
                        {job.salary}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500 mt-2 line-clamp-3">
                    {job.description.slice(0, 280)}
                    {job.description.length > 280 ? '…' : ''}
                  </p>
                  <a
                    href={job.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-blue-600 hover:underline mt-2 inline-block"
                    title="Opens the posting — apply there with your downloaded CV, then click Mark as applied"
                  >
                    Apply on the original posting ↗
                  </a>
                </div>
                <div className="shrink-0 flex flex-col gap-2">
                  <button
                    onClick={() => tailorCV(job)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition"
                  >
                    Tailor CV →
                  </button>
                  {!analysis && (
                    <button
                      onClick={() => analyze(job)}
                      disabled={!!analyzing[job.id]}
                      className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition disabled:opacity-50"
                    >
                      {analyzing[job.id] ? 'Analyzing…' : 'Analyze fit'}
                    </button>
                  )}
                  {applied[job.id] ? (
                    <span className="text-center text-xs text-green-600 font-medium py-1">✓ Applied</span>
                  ) : (
                    <button
                      onClick={() => markApplied(job)}
                      disabled={!!applying[job.id]}
                      className="border border-green-200 text-green-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-50 transition disabled:opacity-50"
                    >
                      {applying[job.id] ? 'Saving…' : 'Mark as applied'}
                    </button>
                  )}
                </div>
              </div>

              {analysis && (
                <div className="mt-4 border-t border-gray-100 pt-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`text-2xl font-bold ${fitColor(analysis.fit_score)}`}>
                      {analysis.fit_score}%
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${WORK_MODE_STYLE[analysis.work_mode]}`}>
                      {analysis.work_mode}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                      {analysis.salary.range}
                      {!analysis.salary.stated && ' (estimated)'}
                    </span>
                    {analysis.visa.likely_needed !== null && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        analysis.visa.likely_needed ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                      }`}>
                        {analysis.visa.likely_needed ? 'Visa likely needed' : 'No visa barrier'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700">{analysis.fit_summary}</p>
                  {analysis.visa.note && (
                    <p className="text-xs text-gray-400">{analysis.visa.note}</p>
                  )}
                  {analysis.matched.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {analysis.matched.map((s, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full">✓ {s}</span>
                      ))}
                    </div>
                  )}
                  {analysis.missing.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {analysis.missing.map((s, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-red-50 text-red-700 rounded-full">✗ {s}</span>
                      ))}
                    </div>
                  )}
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="text-xs font-semibold text-blue-800 mb-1">How to improve your chances</p>
                    <p className="text-sm text-blue-900">{analysis.advice}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {jobs.length > 0 && jobs.length < total && (
        <div className="text-center mt-6">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : `Load more (${jobs.length} of ${total})`}
          </button>
        </div>
      )}
    </div>
  );
};

export default JobsPage;
